import type { ReactNode } from "react";
import type { ResolutionEvent } from "../../domain/types/index.js";
import { StatIcon } from "../../components/StatIcon.js";
import { getCard } from "../../content/lookups.js";

type LoadEvent = Extract<ResolutionEvent, { kind: "load" }>;

type ChangeRow =
  | { key: string; kind: "fatigue" }
  | { key: string; kind: "plain"; icon?: "progress"; label: string; value: string };

export function loadChangeRows(event: LoadEvent): ChangeRow[] {
  const rows: ChangeRow[] = [];
  if (event.fatigueRaw > 0) rows.push({ key: "fatigue", kind: "fatigue" });
  if (event.progressDelta < 0) {
    rows.push({ key: "progress", kind: "plain", icon: "progress", label: "進捗が", value: `${Math.abs(event.progressDelta)} 後退` });
  }
  if (event.nextHandDiscard != null) {
    rows.push({ key: "nextHandDiscard", kind: "plain", label: "次ラウンド開始時", value: `手札を${event.nextHandDiscard}枚捨てる` });
  }
  if (event.addTemporaryCards) {
    const name = getCard(event.addTemporaryCards.cardId)?.name ?? event.addTemporaryCards.cardId;
    rows.push({ key: "addTemporaryCards", kind: "plain", label: "捨て札に追加", value: `${name} ${event.addTemporaryCards.count}枚` });
  }
  if (event.nextActionDrawLimit != null) {
    rows.push({ key: "nextActionDrawLimit", kind: "plain", label: "次ラウンドの施策ドロー", value: `1回につき最大${event.nextActionDrawLimit}枚` });
  }
  return rows;
}

function FormulaChunk({ children }: { children: ReactNode }) {
  return <span className="event-change-chunk">{children}</span>;
}

function FatigueFormula({ event }: { event: LoadEvent }) {
  return (
    <>
      <FormulaChunk>
        <StatIcon name="fatigue" />
        <span>負荷</span>
        <b>{event.fatigueRaw}</b>
      </FormulaChunk>
      {event.blockUsed > 0 && (
        <FormulaChunk>
          <span>−</span>
          <StatIcon name="block" />
          <span>防御</span>
          <b>{event.blockUsed}</b>
        </FormulaChunk>
      )}
      <FormulaChunk>
        <span>= 疲労</span>
        <b className="is-result">+{event.fatigueDealt}</b>
      </FormulaChunk>
    </>
  );
}

export function LoadChanges({ event, staggerMs }: { event: LoadEvent; staggerMs?: number }) {
  const rows = loadChangeRows(event);
  if (rows.length === 0) return null;
  return (
    <div className="event-changes" aria-label="ハプニングによる変化">
      {rows.map((row, i) => {
        const style = staggerMs != null ? { animationDelay: `${(i + 1) * staggerMs}ms` } : undefined;
        return row.kind === "fatigue" ? (
          <span className="event-change is-formula" key={row.key} style={style}>
            <FatigueFormula event={event} />
          </span>
        ) : (
          <span className="event-change" key={row.key} style={style}>
            {row.icon != null && <StatIcon name={row.icon} />}
            <span>{row.label}</span>
            <b>{row.value}</b>
          </span>
        );
      })}
    </div>
  );
}
